<template>
    <div class="space-y-2">
        <form-label name="Pages" />

        <!-- ── Regular pages ── -->
        <div class="pb-2 border-b border-slate-400">
            <form-button
                color="theme"
                title="Add a new page to the design"
                @click="$emit('addPage')"
                class="w-full">
                + Add Page
            </form-button>
        </div>
        <div class="space-y-1">
            <div v-for="page in regularPages" :key="page.haspid"
                 :draggable="page.haspid !== 0"
                 :class="[
                     'px-3 py-2 rounded flex items-center justify-between text-white group',
                     'transition-colors',
                     isActivePage(page) ? 'bg-blue-500 bg-opacity-30 border border-blue-500' : 'bg-slate-400 bg-opacity-20 hover:bg-slate-400 hover:bg-opacity-50',
                     dragOverId === page.haspid && draggedId !== page.haspid ? 'ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-900' : '',
                     draggedId === page.haspid ? 'opacity-40' : '',
                     page.haspid !== 0 ? 'cursor-grab active:cursor-grabbing' : '',
                 ]"
                 :title="'Page ' + page.haspid + (page.comment ? ': ' + page.comment : '')"
                 @dragstart="onDragStart(page, $event)"
                 @dragover.prevent="onDragOver(page)"
                 @dragleave.self="onDragLeave"
                 @drop.prevent="onDrop(page)"
                 @dragend="onDragEnd">
                <div class="flex items-center space-x-2 flex-1 cursor-pointer" @click="selectPage(page)">
                    <span v-if="page.haspid !== 0" class="text-slate-500 text-xs select-none mr-1">⠿</span>
                    <span class="font-semibold">{{ page.haspid === 0 ? 'P0' : 'P' + page.haspid }}</span>
                    <span v-if="page.comment" class="text-xs opacity-75 truncate max-w-[120px]">{{ page.comment }}</span>
                </div>
                <div class="flex items-center space-x-2">
                    <span v-if="isActivePage(page)" class="text-blue-400 text-xs">●</span>
                    <span class="text-xs opacity-50">{{ getPageObjectCount(page) }}</span>
                    <button
                        v-if="canDeletePage(page)"
                        @click.stop="deletePage(page)"
                        class="opacity-30 group-hover:opacity-100 text-red-500 hover:text-red-400 hover:bg-red-500 hover:bg-opacity-20 transition-all px-1.5 py-0.5 rounded font-bold text-sm"
                        title="Delete this page">
                        ✕
                    </button>
                </div>
            </div>
        </div>

        <!-- ── Template pages ── -->
        <div class="pt-2 border-t border-slate-400">
            <div class="flex items-center justify-between mb-1">
                <form-label class="text-xs text-gray-400">Popup Templates</form-label>
                <button
                    class="text-xs text-purple-400 hover:text-purple-300 transition-colors px-1"
                    title="Add a popup template page"
                    @click="showAddTemplate = true">
                    + Template
                </button>
            </div>

            <!-- target page selection dialog -->
            <div v-if="showAddTemplate" class="mb-2 p-2 rounded border border-purple-700 bg-purple-900/20">
                <p class="text-[10px] text-purple-300 mb-1 font-medium">Target page (mandatory)</p>
                <select v-model="newTemplateTarget"
                    class="w-full text-xs rounded px-1 py-1 mb-2"
                    style="background:#1e1b2e;color:#e2e8f0;border:1px solid rgba(167,139,250,0.4)">
                    <option :value="null" disabled>— select target page —</option>
                    <option v-for="p in regularPages" :key="p.haspid" :value="p.haspid">
                        P{{ p.haspid }}{{ p.comment ? ' – ' + p.comment : '' }}
                    </option>
                </select>
                <div class="flex gap-1">
                    <button class="flex-1 py-1 rounded text-[10px] text-white transition-colors"
                        style="background:rgba(139,92,246,0.6)"
                        :disabled="newTemplateTarget === null"
                        @click="confirmAddTemplate">Create</button>
                    <button class="px-2 py-1 rounded text-[10px] text-gray-400 hover:text-white transition-colors"
                        style="background:rgba(255,255,255,0.08)"
                        @click="showAddTemplate = false">Cancel</button>
                </div>
            </div>

            <!-- Template page list -->
            <div class="space-y-1">
                <div v-for="page in templatePages" :key="page.haspid"
                     :class="[
                         'px-3 py-2 rounded flex items-center justify-between text-white group',
                         'border border-dashed transition-colors hover:bg-opacity-50',
                         isActivePage(page)
                             ? 'border-purple-500 bg-purple-500 bg-opacity-20'
                             : 'border-purple-700 bg-purple-900 bg-opacity-10 hover:bg-purple-900 hover:bg-opacity-20'
                     ]"
                     :title="'Template T' + page.haspid + (page.comment ? ': ' + page.comment : '')">
                    <div class="flex items-center space-x-2 flex-1 cursor-pointer" @click="selectPage(page)">
                        <span class="text-purple-400 text-xs font-mono">T</span>
                        <span class="text-xs font-mono">T{{ page.haspid }}</span>
                        <span v-if="page.comment" class="text-xs opacity-60 truncate max-w-[90px]">{{ page.comment }}</span>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span v-if="isActivePage(page)" class="text-purple-400 text-xs">●</span>
                        <span class="text-xs opacity-50">{{ getPageObjectCount(page) }}</span>
                        <button
                            @click.stop="deletePage(page)"
                            class="opacity-30 group-hover:opacity-100 text-red-500 hover:text-red-400 hover:bg-red-500 hover:bg-opacity-20 transition-all px-1.5 py-0.5 rounded font-bold text-sm"
                            title="Delete this template">
                            ✕
                        </button>
                    </div>
                </div>
                <p v-if="templatePages.length === 0" class="text-xs text-gray-500 italic px-1">
                    No templates yet
                </p>
            </div>
        </div>
    </div>

    <ConfirmationModal
        :show="!!pageToDelete"
        :title="deleteTitle"
        :message="deleteMessage"
        @confirm="confirmDelete"
        @cancel="pageToDelete = null" />
</template>

<script>
import HaspPage, { HaspTemplatePage } from '../Hasp/HaspPage';
import ConfirmationModal from './ConfirmationModal.vue';

export default {
    name: 'PageSelector',
    components: { ConfirmationModal },
    props: {
        objectlayer: { type: Object, required: true },
        activePageId: { type: Number, default: null },
    },
    emits: ['selectPage', 'addPage', 'addTemplatePage', 'deletePage', 'reorderPages'],
    data() {
        return {
            showAddTemplate: false,
            newTemplateTarget: null,
            pageToDelete: null,
            draggedId: null,
            dragOverId: null,
        };
    },
    computed: {
        pages() {
            if (!this.objectlayer?.children) return [];
            return this.objectlayer.children
                .filter(c => c instanceof HaspPage)
                .sort((a, b) => {
                    if (a.haspid === 0) return -1;
                    if (b.haspid === 0) return 1;
                    return a.haspid - b.haspid;
                });
        },
        regularPages()  { return this.pages.filter(p => !(p instanceof HaspTemplatePage)); },
        templatePages() { return this.pages.filter(p =>   p instanceof HaspTemplatePage); },
        deleteTitle() {
            if (!this.pageToDelete) return '';
            return this.pageToDelete instanceof HaspTemplatePage ? 'Delete template page' : 'Delete page';
        },
        deleteMessage() {
            if (!this.pageToDelete) return '';
            const p = this.pageToDelete;
            const objectCount = (p.children ?? []).filter(c => c.haspid !== undefined && c.obj !== 'page_select').length;
            const isTemplate  = p instanceof HaspTemplatePage;
            const label       = isTemplate
                ? `Template T${p.haspid}${p.comment ? ' – ' + p.comment : ''}`
                : `Page P${p.haspid}${p.comment ? ' – ' + p.comment : ''}`;
            return `<b>${label}</b> and all ${objectCount} object${objectCount !== 1 ? 's' : ''} on it will be permanently deleted.`;
        },
    },
    methods: {
        confirmAddTemplate() {
            if (this.newTemplateTarget === null) return;
            this.$emit('addTemplatePage', this.newTemplateTarget);
            this.showAddTemplate = false;
            this.newTemplateTarget = null;
        },
        confirmDelete() {
            if (this.pageToDelete) this.$emit('deletePage', this.pageToDelete);
            this.pageToDelete = null;
        },
        selectPage(page)   { this.$emit('selectPage', page); },
        isActivePage(page) {
            return this.objectlayer.activePage?._id === page._id;
        },
        getPageObjectCount(page) {
            if (!page.children) return 0;
            return page.children.filter(c => c.haspid !== undefined && c.obj !== 'page_select').length;
        },
        canDeletePage(page) { return page.haspid !== 0; },
        deletePage(page) {
            if (this.canDeletePage(page)) this.pageToDelete = page;
        },
        onDragStart(page, event) {
            if (page.haspid === 0) { event.preventDefault(); return; }
            this.draggedId = page.haspid;
            event.dataTransfer.effectAllowed = 'move';
        },
        onDragOver(page) {
            if (page.haspid === 0 || this.draggedId === null) return;
            this.dragOverId = page.haspid;
        },
        onDragLeave() {
            this.dragOverId = null;
        },
        onDragEnd() {
            this.draggedId = null;
            this.dragOverId = null;
        },
        onDrop(targetPage) {
            const fromId = this.draggedId;
            this.draggedId = null;
            this.dragOverId = null;
            if (fromId === null || targetPage.haspid === 0 || fromId === targetPage.haspid) return;
            const pages = this.regularPages.filter(p => p.haspid !== 0);
            const fromIdx = pages.findIndex(p => p.haspid === fromId);
            const toIdx   = pages.findIndex(p => p.haspid === targetPage.haspid);
            if (fromIdx === -1 || toIdx === -1) return;
            const reordered = [...pages];
            const [moved] = reordered.splice(fromIdx, 1);
            reordered.splice(toIdx, 0, moved);
            this.$emit('reorderPages', reordered);
        },
    },
};
</script>
